import { useCallback, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useStore } from 'react-redux'
import { AuthContext } from '@/context/auth-context'
import { useEditingLease } from '@/editing-leases/hooks/useEditingLease'
import { RuntimeFeatureFlag } from '@/feature-flags/definitions'
import { useRuntimeFeatureFlag } from '@/feature-flags/useRuntimeFeatureFlag'
import { canEditMedChron } from '@/matter/utils/matter-permissions'
import { selectMatterById } from '@/matter/store/selectors'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import type { RootState } from '@/store/store'
import { MedChronEditingLeaseContext } from '../hooks/useMedChronEditingLease'
import { requestEditingLease } from '../fetch/editing-lease'
import { loadMedChronWorkspace } from '../store/load-workspace'
import { addMedChronAssetListener } from '../store/listeners'
import { MedChronAssetActions } from '../store/slice'
import { EditingResourceKind } from '@/editing-leases/schemas'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { openGlobalToast } from '@/store/slices/global-toast.slice'

export default function MedChronEditingLeaseProvider({ children }: { children: ReactNode }) {
  const { user } = useContext(AuthContext)
  const matterId = useAppSelector((state) => state.mattersState.activeMatterId)
  return (
    <WorkspaceLease key={`${user?.uid}:${matterId}`} matterId={matterId ?? undefined} userId={user?.uid}>
      {children}
    </WorkspaceLease>
  )
}

function WorkspaceLease({ children, matterId, userId }: { children: ReactNode; matterId?: string; userId?: string }) {
  const dispatch = useAppDispatch()
  const store = useStore<RootState>()
  const matter = useAppSelector((state) => selectMatterById(state, { matterId }))
  const editor = useRuntimeFeatureFlag(RuntimeFeatureFlag.COLLAB_EDITOR_ROLE)
  const api = useMemo(
    () => ({
      status: () => requestEditingLease(matterId!, 'status'),
      acquire: (age?: number) => requestEditingLease(matterId!, 'acquire', age),
      renew: (age?: number) => requestEditingLease(matterId!, 'renew', age),
    }),
    [matterId]
  )
  const refresh = useCallback(
    (isCurrent: () => boolean) => loadMedChronWorkspace({ matterId: matterId!, force: true }, { dispatch, getState: store.getState, isCurrent }),
    [matterId, dispatch, store]
  )
  const onStartEditingFailure = useCallback(
    () =>
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: 'Something went wrong. Try refreshing the page.', durationMs: kToastDurationMediumMs })),
    [dispatch]
  )
  const lease = useEditingLease({
    resource: { kind: EditingResourceKind.MED_CHRON, resource_id: matterId ?? '', matter_id: matterId ?? '' },
    eligible: canEditMedChron(matter, editor.enabled && !editor.loading),
    ready: Boolean(userId && matterId && !editor.loading),
    enabled: editor.enabled,
    api,
    refresh,
    onStartEditingFailure,
  })
  const { handleDenial } = lease
  useEffect(
    () =>
      dispatch(
        addMedChronAssetListener({
          actionCreator: MedChronAssetActions.editingLeaseRejected,
          effect: (action) => {
            handleDenial(action.payload)
          },
        })
      ),
    [dispatch, handleDenial]
  )
  return (
    <MedChronEditingLeaseContext.Provider value={lease}>
      <div
        className="flex flex-col min-h-0 h-full w-full"
        onWheelCapture={lease.recordActivity}
        onTouchMoveCapture={lease.recordActivity}
        onKeyDownCapture={lease.recordActivity}
        onPointerDownCapture={lease.recordActivity}
      >
        {children}
      </div>
    </MedChronEditingLeaseContext.Provider>
  )
}
